/*
  HauteFlair size memory.
  Saves the shopper's band and cup in this browser only (localStorage 'hf-size').
  Used by:
  - [data-hf-size-slot]       mega menu bar, mobile menu: "Your size 34DD · Shop your size" or a fit finder link
  - [data-hf-size-form]       size finder forms with [data-hf-band] and [data-hf-cup] selects
  - [data-hf-set-size="34DD"] quick size buttons
  - [data-hf-size-chip]       collection size card. The section passes the real filter parameter names
                              from collection.filters (data-band-param / data-cup-param); they are cached
                              and reused by the menu and size finder links.
  - [data-hf-sister]          product page tip; data-bands / data-cups list what the style is made in
  - product variant picker    preselects the saved band and cup when the URL has no ?variant
  - predictive search         "Shop 34DD bras" suggestion when the query is a size
  Nothing here is required for checkout. If storage is blocked, every feature falls back to its default state.
*/
(() => {
  const cfg = window.hfConfig || {};
  if (!cfg.sizeEnabled) return;

  const KEY = 'hf-size';
  const PARAM_KEY = 'hf-size-params';
  const bands = (cfg.bands || []).map((b) => String(b).trim()).filter(Boolean);
  const cups = (cfg.cups || []).map((c) => String(c).trim().toUpperCase()).filter(Boolean);
  const SIZE_RE = new RegExp(`^(${bands.join('|')})\\s*(${[...cups].sort((a, b) => b.length - a.length).join('|')})$`, 'i');
  const root = (cfg.rootUrl || '/').replace(/\/$/, '');

  const store = {
    get(key) {
      try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage blocked */ }
    }
  };
  const valid = (s) => s && bands.includes(String(s.band)) && cups.includes(String(s.cup).toUpperCase());
  const clean = (s) => (valid(s) ? { band: String(s.band), cup: String(s.cup).toUpperCase() } : null);

  let size = clean(store.get(KEY));
  const label = (s = size) => (s ? `${s.band}${s.cup}` : '');

  // Filter parameter names: learned from collection.filters on collection pages, else built from settings.
  const learned = store.get(PARAM_KEY) || {};
  const params = {
    band: learned.band || `filter.v.option.${String(cfg.bandOption || 'Band Size').toLowerCase()}`,
    cup: learned.cup || `filter.v.option.${String(cfg.cupOption || 'Cup Size').toLowerCase()}`
  };
  const learnParams = (band, cup) => {
    if (!band || !cup) return;
    params.band = band;
    params.cup = cup;
    store.set(PARAM_KEY, { band, cup });
  };

  const localize = (path) => {
    if (!path) path = cfg.sizeCollection || '/collections/all';
    if (/^https?:/i.test(path)) return path;
    if (root && path.startsWith('/') && !path.startsWith(root + '/')) return root + path;
    return path;
  };

  // Build a link that applies (or clears) the size filters. When `keepCurrent` is set and the link
  // points at the current page, other filters and the sort order are kept.
  const filterUrl = (path, s = size, keepCurrent = false) => {
    const target = new URL(localize(path), window.location.origin);
    const url = keepCurrent && target.pathname === window.location.pathname ? new URL(window.location.href) : target;
    url.searchParams.delete(params.band);
    url.searchParams.delete(params.cup);
    url.searchParams.delete('page');
    if (s) {
      url.searchParams.set(params.band, s.band);
      url.searchParams.set(params.cup, s.cup);
      if (!url.searchParams.has('filter.v.availability')) url.searchParams.set('filter.v.availability', '1');
    }
    return url.pathname + url.search;
  };

  const set = (band, cup) => {
    const next = clean({ band, cup });
    if (!next) return false;
    size = next;
    store.set(KEY, size);
    document.dispatchEvent(new CustomEvent('hf:size-change', { detail: size }));
    render();
    return true;
  };

  const parse = (text) => {
    const m = String(text || '').replace(/\s+/g, '').match(SIZE_RE);
    return m ? { band: m[1], cup: m[2].toUpperCase() } : null;
  };

  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => {
      if (k === 'text') node.textContent = v;
      else node.setAttribute(k, v);
    });
    children.forEach((c) => node.append(c));
    return node;
  };

  /* ---------- Slots: menu bar, mobile menu ---------- */
  const renderSlots = () => {
    document.querySelectorAll('[data-hf-size-slot]').forEach((slot) => {
      const path = slot.dataset.hfCollection || cfg.sizeCollection;
      const link = size
        ? el('a', { href: filterUrl(path) }, ['Your size ', el('b', { text: label() }), ' · Shop your size'])
        : el('a', { href: localize(cfg.fitFinder), text: 'Find your size in 2 minutes' });
      slot.replaceChildren(link);
    });
  };

  /* ---------- Collection quick filter bar: "My size" toggle ---------- */
  // A band and a cup picked in the bar (one of each) become the saved size.
  const learnFromUrl = () => {
    const card = document.querySelector('[data-hf-size-chip]');
    if (!card) return;
    learnParams(card.dataset.bandParam, card.dataset.cupParam);
    const q = new URLSearchParams(window.location.search);
    const b = q.getAll(params.band);
    const c = q.getAll(params.cup);
    if (b.length !== 1 || c.length !== 1) return;
    const next = clean({ band: b[0], cup: c[0] });
    if (next && label(next) !== label()) {
      size = next;
      store.set(KEY, size);
    }
  };
  const renderChips = () => {
    document.querySelectorAll('[data-hf-size-chip]').forEach((card) => {
      learnParams(card.dataset.bandParam, card.dataset.cupParam);
      const path = card.dataset.hfCollection || window.location.pathname;
      const q = new URLSearchParams(window.location.search);
      const active = Boolean(size) && q.getAll(params.band).join() === size.band && q.getAll(params.cup).join() === size.cup;
      if (!size) {
        card.replaceChildren(el('a', { class: 'hfr-qf__mine-link', href: localize(cfg.fitFinder), text: 'Find your size' }));
      } else {
        card.replaceChildren(
          el('a', {
            class: `hfr-qf__toggle${active ? ' is-on' : ''}`,
            rel: 'nofollow',
            href: active ? filterUrl(path, null, true) : filterUrl(path, size, true),
            'aria-pressed': String(active),
            title: active ? `Showing styles made in ${label()}` : `Show only styles made in ${label()}`
          }, [el('span', { class: 'hfr-qf__box', 'aria-hidden': 'true' }), 'My size ', el('b', { text: label() })])
        );
      }
      card.hidden = false;
    });
  };

  /* ---------- Size finder forms and quick buttons ---------- */
  const bindForms = () => {
    document.querySelectorAll('[data-hf-size-form]').forEach((form) => {
      if (form.dataset.hfBound) return;
      form.dataset.hfBound = '1';
      const b = form.querySelector('[data-hf-band]');
      const c = form.querySelector('[data-hf-cup]');
      if (size) {
        if (b) b.value = size.band;
        if (c) c.value = size.cup;
      }
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!b || !c || !b.value || !c.value) return;
        if (set(b.value, c.value)) window.location.href = filterUrl(form.dataset.hfCollection || cfg.sizeCollection);
      });
    });
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-hf-set-size]');
    if (!btn) return;
    const s = parse(btn.dataset.hfSetSize);
    if (!s) return;
    e.preventDefault();
    set(s.band, s.cup);
    if (btn.dataset.hfGo !== 'false') window.location.href = filterUrl(btn.dataset.hfCollection || cfg.sizeCollection);
  });

  /* ---------- Product page: sister size tip ---------- */
  const sisterFor = (s, styleBands, styleCups) => {
    const i = cups.indexOf(s.cup);
    const tries = [
      [String(Number(s.band) - 2), cups[i + 1]],
      [String(Number(s.band) + 2), cups[i - 1]]
    ];
    return tries.find(([b, c]) => b && c && styleBands.includes(b) && styleCups.includes(c));
  };
  const renderSister = () => {
    document.querySelectorAll('[data-hf-sister]').forEach((tip) => {
      const sb = (tip.dataset.bands || '').split(',').map((b) => b.trim()).filter(Boolean);
      const sc = (tip.dataset.cups || '').split(',').map((c) => c.trim().toUpperCase()).filter(Boolean);
      if (!tip.dataset.hfDefault) tip.dataset.hfDefault = tip.innerHTML;
      if (!size || !sb.length || (sb.includes(size.band) && sc.includes(size.cup))) {
        tip.innerHTML = tip.dataset.hfDefault;
        return;
      }
      const sis = sisterFor(size, sb, sc);
      const lead = el('b', { text: `${label()} isn't made in this style.` });
      if (sis) {
        tip.replaceChildren(lead, ` Try your sister size ${sis[0]}${sis[1]}: the same cup volume on a different band.`);
      } else {
        tip.replaceChildren(lead, ' ', el('a', { href: filterUrl(cfg.sizeCollection), text: `See styles made in ${label()}` }), '.');
      }
    });
  };

  /* ---------- Product page: preselect the saved size ---------- */
  // Waits for the stock <variant-picker> element, picks the band, then waits for the picker's own
  // on:variant:change event before picking the cup, so the picker always sees both choices.
  const findInput = (picker, optionName, value) => {
    const fs = picker.querySelector(`.option-selector[data-option="${CSS.escape(optionName)}"]`);
    if (!fs) return null;
    const input = [...fs.querySelectorAll('input.js-option')].find((i) => i.value.toUpperCase() === String(value).toUpperCase());
    if (!input || input.classList.contains('is-unavailable')) return null;
    return input;
  };
  const markMine = (picker) => {
    if (!size) return;
    [[cfg.bandOption, size.band], [cfg.cupOption, size.cup]].forEach(([name, value]) => {
      const fs = picker.querySelector(`.option-selector[data-option="${CSS.escape(name)}"]`);
      if (!fs) return;
      fs.querySelectorAll('input.js-option').forEach((i) => {
        const lab = fs.querySelector(`label[for="${CSS.escape(i.id)}"]`);
        if (lab) lab.classList.toggle('hfr-is-mine', i.value.toUpperCase() === String(value).toUpperCase());
      });
    });
  };
  const getPicker = () => document.querySelector('.product-info variant-picker, variant-picker');
  // Clicks an option and resolves after the picker reports the change (or after 1.5s).
  const clickWait = (input) =>
    new Promise((resolve) => {
      if (!input || input.checked) {
        resolve();
        return;
      }
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        requestAnimationFrame(() => resolve());
      };
      document.addEventListener('on:variant:change', finish, { once: true });
      setTimeout(finish, 1500);
      input.click();
    });
  // Colour-type options get their first available value, so only the size is left to choose.
  // Size options are never guessed.
  const SAFE_OPTION = /colou?r|shade|print|pattern|finish/i;
  const preselect = () => {
    if (!cfg.isProduct || cfg.designMode || !window.customElements) return;
    if (new URLSearchParams(window.location.search).has('variant')) return;
    customElements.whenDefined('variant-picker').then(async () => {
      let picker = getPicker();
      if (!picker) return;
      markMine(picker);
      const names = [...picker.querySelectorAll('.option-selector[data-option]')]
        .map((fs) => fs.dataset.option)
        .filter((n) => n !== cfg.bandOption && n !== cfg.cupOption && SAFE_OPTION.test(n));
      for (const name of names) {
        picker = getPicker();
        const fs = picker && picker.querySelector(`.option-selector[data-option="${CSS.escape(name)}"]`);
        if (!fs || fs.querySelector('input.js-option:checked')) continue;
        const first = [...fs.querySelectorAll('input.js-option')].find((i) => !i.classList.contains('is-unavailable'));
        await clickWait(first);
      }
      if (!size) return;
      picker = getPicker();
      if (!picker) return;
      const band = findInput(picker, cfg.bandOption, size.band);
      if (!band) return;
      await clickWait(band);
      picker = getPicker();
      if (!picker) return;
      markMine(picker);
      await clickWait(findInput(picker, cfg.cupOption, size.cup));
      markMine(getPicker() || picker);
    });
  };
  // Save the size when the shopper picks band and cup by hand on a product page.
  document.addEventListener('change', (e) => {
    const input = e.target.closest('.option-selector input.js-option');
    if (!input || !cfg.isProduct || !e.isTrusted) return;
    const picker = input.closest('variant-picker');
    if (!picker) return;
    const bandFs = picker.querySelector(`.option-selector[data-option="${CSS.escape(cfg.bandOption)}"]`);
    const cupFs = picker.querySelector(`.option-selector[data-option="${CSS.escape(cfg.cupOption)}"]`);
    const b = bandFs && bandFs.querySelector('input:checked');
    const c = cupFs && cupFs.querySelector('input:checked');
    if (b && c) set(b.value, c.value);
  });

  /* ---------- Predictive search: size suggestion ---------- */
  const bindSearch = () => {
    document.querySelectorAll('predictive-search').forEach((ps) => {
      if (ps.dataset.hfBound) return;
      ps.dataset.hfBound = '1';
      const input = ps.querySelector('.js-search-input');
      const results = ps.querySelector('.js-search-results');
      if (!input || !results) return;
      const inject = () => {
        const s = parse(input.value);
        const old = results.querySelector('.hfr-size-sugg');
        if (old) old.remove();
        if (!s || !results.firstElementChild) return;
        const a = el('a', { class: 'hfr-size-sugg', href: filterUrl(cfg.sizeCollection, s) }, [
          el('span', {}, [el('span', { class: 'hfr-eyebrow', text: 'Size' }), el('b', { text: `Shop ${s.band}${s.cup} bras` })]),
          el('span', { 'aria-hidden': 'true', text: '→' })
        ]);
        a.addEventListener('click', () => set(s.band, s.cup));
        results.prepend(a);
      };
      new MutationObserver(() => {
        if (!results.querySelector('.hfr-size-sugg')) inject();
      }).observe(results, { childList: true });
    });
  };

  function render() {
    renderChips();
    renderSlots();
    renderSister();
  }

  const init = () => {
    learnFromUrl();
    render();
    bindForms();
    bindSearch();
    preselect();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);

  window.HFSize = { get: () => size, set, filterUrl, parse };
})();

/* HauteFlair: Add to Bag stays active before a size is picked (sections/main-product.liquid).
   When it is pressed with an option still open, stop the add, point to the first missing option
   and say what to pick. Runs in the capture phase so it fires before the theme's product form. */
(function () {
  function pickerFor(form) {
    var scope = form.closest('.shopify-section, quick-add-drawer, .drawer') || document;
    return scope.querySelector('variant-picker');
  }
  function isPicked(o) {
    if (o.querySelector('input:checked')) return true;
    var s = o.querySelector('select');
    return !!(s && s.value);
  }
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    var id = form.querySelector('input[name="id"]');
    if (!id || id.value) return;
    var picker = pickerFor(form);
    if (!picker) return;
    var missing = Array.prototype.filter.call(picker.querySelectorAll('.option-selector'), function (o) { return !isPicked(o); });
    if (!missing.length) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    missing.forEach(function (o) {
      o.classList.add('hfr-pick-needed');
      if (!o.querySelector('.hfr-pick-msg')) {
        var lab = o.querySelector('legend.label, label.label, legend, .label');
        var name = lab ? (lab.textContent || '').split(':')[0].trim().toLowerCase() : 'an option';
        var msg = document.createElement('p');
        msg.className = 'hfr-pick-msg';
        msg.setAttribute('role', 'alert');
        msg.textContent = 'Please select a ' + name;
        var anchor = lab && lab.closest('[data-sf-row]') ? lab.closest('[data-sf-row]') : lab;
        if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(msg, anchor.nextSibling);
        else o.insertBefore(msg, o.firstChild);
      }
    });
    var first = missing[0];
    var r = first.getBoundingClientRect();
    if (r.top < 90 || r.bottom > window.innerHeight) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var input = first.querySelector('input:not([disabled]), select');
    if (input) input.focus({ preventScroll: true });
  }, true);
  document.addEventListener('change', function (e) {
    var o = e.target && e.target.closest ? e.target.closest('.option-selector') : null;
    if (!o) return;
    o.classList.remove('hfr-pick-needed');
    var m = o.querySelector('.hfr-pick-msg');
    if (m) m.remove();
  }, true);
})();

/* HauteFlair: keep the active thumbnail in view when the thumbnails run in a vertical rail
   (hf-brand.css, slider layout from 1024px). The theme only scrolls them sideways. */
(function () {
  function init() {
    document.querySelectorAll('.product-media--slider .media-thumbs').forEach(function (list) {
      if (list.dataset.hfrRail) return;
      list.dataset.hfrRail = '1';
      new MutationObserver(function () {
        if (getComputedStyle(list).flexDirection !== 'column') return;
        var a = list.querySelector('.media-thumbs__btn.is-active');
        if (!a) return;
        var li = a.parentElement;
        if (li.offsetTop < list.scrollTop || li.offsetTop + li.offsetHeight > list.scrollTop + list.clientHeight) {
          list.scrollTo({ top: Math.max(0, li.offsetTop - 8), behavior: 'smooth' });
        }
      }).observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
